import type { RichDoc } from "@sebas/core";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useImperativeHandle, useRef, type Ref } from "react";
import { uploadFile } from "@/lib/attachments";
import { cn } from "@/lib/cn";
import { SebasAudio } from "./SebasAudio";
import { SebasImage } from "./SebasImage";

export type NoteEditorHandle = {
  insertAudio: (attachmentId: string, durationMs: number) => void;
  insertImageFiles: (files: File[]) => void;
};

/**
 * The note body editor.
 *
 * Owns no save policy — it reports changes upward so debounce/autosave lives in
 * one place. It does own media insertion, because paste and drop are editor
 * events and have to be intercepted here.
 */
export function NoteEditor({
  initialContent,
  editable = true,
  noteId,
  onChange,
  onUploadError,
  handleRef,
  className,
}: {
  initialContent: RichDoc;
  editable?: boolean;
  noteId: string;
  onChange?: (doc: RichDoc) => void;
  onUploadError?: (message: string) => void;
  handleRef?: Ref<NoteEditorHandle>;
  className?: string;
}) {
  // The paste/drop handlers live inside `editorProps`, which is evaluated while
  // `editor` is still being created — so they reach it through a ref instead.
  const editorRef = useRef<Editor | null>(null);

  const uploadAndInsert = (editor: Editor, files: File[]) => {
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return false;

    for (const file of images) {
      void uploadFile(file, "image", { filename: file.name, noteId })
        .then((attachmentId) =>
          editor.chain().focus().insertSebasImage({ attachmentId, alt: file.name }).run(),
        )
        .catch((e) => onUploadError?.(e instanceof Error ? e.message : String(e)));
    }
    return true;
  };

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: "Start writing\u2026" }),
      TaskList,
      TaskItem.configure({ nested: true }),
      SebasImage,
      SebasAudio,
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: initialContent,
    onUpdate: ({ editor }) => onChange?.(editor.getJSON() as RichDoc),
    editorProps: {
      attributes: {
        class: cn("prose-sebas min-h-[40vh] max-w-none focus:outline-none", !editable && "opacity-80"),
      },
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []);
        const ed = editorRef.current;
        return files.length > 0 && ed ? uploadAndInsert(ed, files) : false;
      },
      handleDrop: (_view, event) => {
        const files = Array.from((event as DragEvent).dataTransfer?.files ?? []);
        const ed = editorRef.current;
        return files.length > 0 && ed ? uploadAndInsert(ed, files) : false;
      },
    },
  });

  editorRef.current = editor;

  useImperativeHandle(
    handleRef,
    () => ({
      insertAudio: (attachmentId, durationMs) =>
        editor?.chain().focus().insertSebasAudio({ attachmentId, durationMs }).run(),
      insertImageFiles: (files) => editor && uploadAndInsert(editor, files),
    }),
    [editor],
  );

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  return <EditorContent editor={editor} className={className} />;
}
